import axios, { AxiosInstance } from "axios";
import { baseURL } from "../constants";
import { getUserCountryCode } from "@/i18n";
import { store } from "@/store/store";
import {
    resetTokens,
    setAccessToken,
    setRefreshToken,
} from "@/store/user/slice";
import { AppRoutes } from "@/types/routes";

export const registerInterceptor = (apiClient: AxiosInstance) =>
    // Add request interceptor
    apiClient.interceptors.request.use(
        async config => {
            // Safely parse a localStorage token value, guarding against null/"null"/empty
            const parseToken = (key: string) => {
                const raw = window.localStorage.getItem(key);
                if (!raw || raw === "null") return null;
                try { return JSON.parse(raw); } catch { return null; }
            };

            const parsedAccess = parseToken("accessToken");
            const parsedRefresh = parseToken("refreshToken");
            const { token, expires } = parsedAccess || {};
            const { token: refreshToken } = parsedRefresh || {};

            // Only set Authorization header when we actually have a token
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            // Detect and set the X-Language header
            const detectedCountryCode = getUserCountryCode();
            config.headers["X-Country-Code"] = detectedCountryCode;

            // Get tenant override
            const tenantOverride =
                window.localStorage.getItem("tenantOverride") || null;
            if (tenantOverride) {
                config.headers["X-Tenant-Override"] = tenantOverride;
            }

            if (token && expires && Date.now() >= new Date(expires).getTime()) {
                const dispatch = store.dispatch;

                try {
                    // Call your token refresh endpoint
                    const response = await axios.post(
                        `${baseURL}auth/refresh-tokens`,
                        {
                            refreshToken,
                        },
                    );

                    // Update the token in local storage
                    localStorage.setItem(
                        "accessToken",
                        JSON.stringify(response.data.access),
                    );

                    localStorage.setItem(
                        "refreshToken",
                        JSON.stringify(response.data.refresh),
                    );

                    dispatch(setAccessToken(response.data.access));
                    dispatch(setRefreshToken(response.data.refresh));

                    // Update the request headers with the new token
                    config.headers.Authorization = `Bearer ${response.data.access.token}`;
                } catch (error) {
                    // Handle error refreshing token (e.g., redirect to login page)
                    console.error("Error refreshing token:", error);
                    // Update the token in local storage
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("refreshToken");
                    dispatch(resetTokens());
                    // Redirect to login page or show an error message
                    window.location.href = "/" + AppRoutes.Login;
                }
            }
            return config;
        },
        error => {
            return Promise.reject(error);
        },
    );
